import { MenuItem } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";

import { type DomIconSpec, closePanelIconSpec, moreOptionsIconSpec } from "../icons";
import { type PanelType } from "../plugins/panel";
import { getActivePanelId, toolbarPluginKey } from "../plugins/toolbar";

import { t } from "i18n/i18n";

/**
 * Creates a toolbar MenuItem that navigates from sourcePanel to targetPanel.
 * Visible only when sourcePanel is active.
 */
export function createGoToPanelItem(
  targetPanel: PanelType,
  sourcePanel: PanelType,
  icon: DomIconSpec,
  title: string,
  cssClass?: string,
): MenuItem {
  return new MenuItem({
    title,
    icon, // prosemirror-menu's path spec renders filled shapes only; stroke attributes require a DOM node
    class: cssClass,
    select(state: EditorState) {
      return getActivePanelId(state) === sourcePanel;
    },
    run(state, dispatch, view, event) {
      const isKeyboard = (event as MouseEvent | undefined)?.detail === 0; // detail = click count
      view?.focus(); // keeps :focus-within alive during dispatch; no-op for mouse
      dispatch(
        state.tr.setMeta(toolbarPluginKey, { activePanelId: targetPanel, shouldFocus: isKeyboard }),
      );
    },
  });
}

export const createMoreOptionsItem = createGoToPanelItem(
  "moreOptions",
  "main",
  moreOptionsIconSpec,
  t("worksheet.rich_text_editor.show_more_options"),
);

export function createCloseItemForPanel(sourcePanel: PanelType): MenuItem {
  return createGoToPanelItem(
    "main",
    sourcePanel,
    closePanelIconSpec,
    t("worksheet.rich_text_editor.close_panel"),
    "close-panel-item",
  );
}
